import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  // Page UI Icons
  Code, FileSearch, PieChart, ChevronDown, ChevronUp, Loader2,
  TextQuote, Table, Bug, Bot, X, Save,
  // Quick Action Icons
  BarChart3, Package, Users, Building2, AlertTriangle, FileText, Search,
  ShoppingCart, TrendingUp, Activity, Zap, Settings, Database, DollarSign,
  Percent, Calendar, Clock, MessageSquare, Shield, FileSpreadsheet,
  Briefcase, Truck, Inbox, Cpu, Globe, Layers, List, ClipboardList,
  FileCheck, HelpCircle, Bell, Bookmark, Tag, CheckCircle2, Info,
  TrendingDown, Heart, Star, Play, Terminal, ArrowRight,
  Calculator, CreditCard, Wallet, Gift, Award, Key, Lock, Unlock, Eye,
  EyeOff, Filter, Download, Upload, Share2, Link, Mail, Phone, UserCheck,
  UserX, MapPin, Map, Compass, Printer, Trash2, Edit3, Plus, Minus,
  Folder, FolderOpen, HardDrive, Cloud, CloudDownload, CloudUpload,
  Book, BookOpen, Tags, Wrench, Sliders, History, RefreshCw
} from 'lucide-react';

const ICON_MAP = {
  'BarChart3': BarChart3,
  'Package': Package,
  'Users': Users,
  'Building2': Building2,
  'AlertTriangle': AlertTriangle,
  'FileText': FileText,
  'Search': Search,
  'ShoppingCart': ShoppingCart,
  'TrendingUp': TrendingUp,
  'Activity': Activity,
  'Zap': Zap,
  'Settings': Settings,
  'Database': Database,
  'DollarSign': DollarSign,
  'Percent': Percent,
  'Calendar': Calendar,
  'Clock': Clock,
  'MessageSquare': MessageSquare,
  'Shield': Shield,
  'FileSpreadsheet': FileSpreadsheet,
  'Briefcase': Briefcase,
  'Truck': Truck,
  'Inbox': Inbox,
  'Cpu': Cpu,
  'Globe': Globe,
  'Layers': Layers,
  'List': List,
  'ClipboardList': ClipboardList,
  'FileCheck': FileCheck,
  'HelpCircle': HelpCircle,
  'Bell': Bell,
  'Bookmark': Bookmark,
  'Tag': Tag,
  'CheckCircle2': CheckCircle2,
  'Info': Info,
  'TrendingDown': TrendingDown,
  'Heart': Heart,
  'Star': Star,
  'Play': Play,
  'Terminal': Terminal,
  'Calculator': Calculator,
  'CreditCard': CreditCard,
  'Wallet': Wallet,
  'Gift': Gift,
  'Award': Award,
  'Key': Key,
  'Lock': Lock,
  'Unlock': Unlock,
  'Eye': Eye,
  'EyeOff': EyeOff,
  'Filter': Filter,
  'Download': Download,
  'Upload': Upload,
  'Share2': Share2,
  'Link': Link,
  'Mail': Mail,
  'Phone': Phone,
  'UserCheck': UserCheck,
  'UserX': UserX,
  'MapPin': MapPin,
  'Map': Map,
  'Compass': Compass,
  'Printer': Printer,
  'Trash2': Trash2,
  'Edit3': Edit3,
  'Plus': Plus,
  'Minus': Minus,
  'Folder': Folder,
  'FolderOpen': FolderOpen,
  'HardDrive': HardDrive,
  'Cloud': Cloud,
  'CloudDownload': CloudDownload,
  'CloudUpload': CloudUpload,
  'Book': Book,
  'BookOpen': BookOpen,
  'Tags': Tags,
  'Wrench': Wrench,
  'Sliders': Sliders,
  'History': History,
  'RefreshCw': RefreshCw
};

const COLOR_MAP = {
  'red':    'bg-red-500',
  'cyan':   'bg-[#00B7D4]',
  'blue':   'bg-blue-500',
  'green':  'bg-green-500',
  'yellow': 'bg-yellow-500',
  'orange': 'bg-orange-500',
  'purple': 'bg-purple-500',
  'gray':   'bg-gray-500'
};

const SELECTED_COLOR_CLASSES = {
  'blue':   'bg-blue-500/20 border-blue-500 text-blue-400',
  'red':    'bg-red-500/20 border-red-500 text-red-400',
  'green':  'bg-green-500/20 border-green-500 text-green-400',
  'yellow': 'bg-yellow-500/20 border-yellow-500 text-yellow-400',
  'orange': 'bg-orange-500/20 border-orange-500 text-orange-400',
  'cyan':   'bg-cyan-500/20 border-cyan-500 text-cyan-400',
  'purple': 'bg-purple-500/20 border-purple-500 text-purple-400',
  'gray':   'bg-gray-500/20 border-gray-500 text-gray-400'
};
const API_URL = process.env.REACT_APP_BACKEND_URL || '';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// ── QueryResultTable Component ────────
const QueryResultTable = ({ data }) => {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: null, direction: 'asc' });
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const cols = useMemo(() => {
    if (!data || data.length === 0) return [];
    return Object.keys(data[0]).filter(key => key !== '_id');
  }, [data]);

  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    let result = [...data];
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(row => cols.some(col => String(row[col] || '').toLowerCase().includes(s)));
    }
    if (sort.key) {
      result.sort((a, b) => {
        const va = a[sort.key]; const vb = b[sort.key];
        if (va === vb) return 0;
        const res = va < vb ? -1 : 1;
        return sort.direction === 'asc' ? res : -res;
      });
    }
    return result;
  }, [data, search, sort, cols]);

  if (!data || data.length === 0) return null;
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const pagedData = filteredData.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input type="text" placeholder="Buscar en resultados..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-[#0F1117] border border-gray-800 rounded-lg py-2 pl-10 pr-4 text-sm text-gray-300 focus:outline-none focus:border-orange-500/50" />
        </div>
        <div className="flex items-center gap-2">
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="p-2 bg-gray-800 rounded-lg disabled:opacity-30 hover:bg-gray-700 transition-colors"><ChevronDown className="w-4 h-4 rotate-90" /></button>
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Pág {page} de {totalPages || 1}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="p-2 bg-gray-800 rounded-lg disabled:opacity-30 hover:bg-gray-700 transition-colors"><ChevronDown className="w-4 h-4 -rotate-90" /></button>
        </div>
      </div>
      <div className="rounded-xl border border-gray-800 overflow-hidden bg-[#0F1117] shadow-inner">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-xs text-left">
            <thead>
              <tr className="bg-[#1A1D27] border-b border-gray-800 text-gray-500 uppercase font-bold">
                {cols.map(col => (
                  <th key={col} onClick={() => setSort({ key: col, direction: sort.key === col && sort.direction === 'asc' ? 'desc' : 'asc' })}
                    className="px-4 py-3 cursor-pointer hover:text-orange-500 transition-colors">
                    <div className="flex items-center gap-2">{col} {sort.key === col && (sort.direction === 'asc' ? '↑' : '↓')}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {pagedData.map((row, i) => (
                <tr key={i} className="hover:bg-gray-800/20 transition-colors">
                  {cols.map(col => (<td key={col} className="px-4 py-3 font-mono text-gray-400">{typeof row[col] === 'object' ? JSON.stringify(row[col]) : String(row[col] || '-')}</td>))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const AdminChatTestPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [endpointsLoading, setEndpointsLoading] = useState(true);
  const [endpoints, setEndpoints] = useState([]);
  const [selectedEndpoints, setSelectedEndpoints] = useState([]);
  const [debugMode, setDebugMode] = useState(false);
  const [duration, setDuration] = useState(null);
  const [isExecutionModalOpen, setIsExecutionModalOpen] = useState(false);
  const [countdown, setCountdown] = useState(null);

  const [quickActionModalOpen, setQuickActionModalOpen] = useState(false);
  const [iconSearch, setIconSearch] = useState('');
  const [qaForm, setQaForm] = useState({ title: '', description: '', icon: 'BarChart3', color: 'blue', cache_ttl_ms: 3600000, area: '', prompt: '' });
  const [quickActions, setQuickActions] = useState([]);
  // Áreas dinámicas desde la DB — mismo método que "Gestionar Áreas" (AdminUsersPage)
  const [areasDisponibles, setAreasDisponibles] = useState([]);
  const defaultArea = areasDisponibles[0]?.name || '';
  const [loadingActions, setLoadingActions] = useState(false);
  const [editingActionId, setEditingActionId] = useState(null);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);

  const filteredIcons = useMemo(() => {
    return Object.entries(ICON_MAP).filter(([name]) =>
      name.toLowerCase().includes(iconSearch.toLowerCase())
    ).sort((a, b) => a[0].localeCompare(b[0]));
  }, [iconSearch]);

  const fetchQuickActions = useCallback(async () => {
    if (!token) return;
    setLoadingActions(true);
    try {
      const res = await fetch(`${API_URL}/api/ai-config/quick-actions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setQuickActions(data);
      } else {
        throw new Error('Error al cargar Quick Actions');
      }
    } catch (err) {
      console.error('Error fetching quick actions:', err);
      toast.error('No se pudieron cargar las Quick Actions');
    } finally {
      setLoadingActions(false);
    }
  }, [token]);

  const fetchAreas = useCallback(async () => {
    if (!token) return;
    try {
      const resp = await fetch(`${API_URL}/api/admin/areas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) return;
      const data = await resp.json();
      setAreasDisponibles(data);
      // Si el formulario no tiene área asignada, usar la primera disponible
      setQaForm(f => (f.area ? f : { ...f, area: data[0]?.name || '' }));
    } catch { /* silencioso */ }
  }, [token]);

  useEffect(() => {
    fetchQuickActions();
    fetchAreas();
  }, [fetchQuickActions, fetchAreas]);

  useEffect(() => {
    if (!quickActionModalOpen) {
      setIconSearch('');
      // Clean up editing action ID when closed (if user cancels)
      // wait, we shouldn't reset editingActionId immediately here if it's open, but when it's closed it's safe.
    }
  }, [quickActionModalOpen]);

  const handleNewQuickAction = () => {
    setEditingActionId(null);
    setQaForm({
      title: '',
      description: '',
      icon: 'BarChart3',
      color: 'blue',
      cache_ttl_ms: 3600000,
      area: defaultArea,
      prompt: ''
    });
    setQuestion('');
    if (endpoints && endpoints.length > 0) {
      setSelectedEndpoints(endpoints.map(ep => ep.id));
    } else {
      setSelectedEndpoints([]);
    }
    toast.info('Probador reiniciado. Escribe tu consulta y selecciona colecciones, luego haz click en GUARDAR COMO QA.');
  };

  const handleLoadIntoTester = (action) => {
    setEditingActionId(action.id);
    setQaForm({
      title: action.title || '',
      description: action.description || '',
      icon: action.icon || 'BarChart3',
      color: action.color || 'blue',
      cache_ttl_ms: action.cache_ttl_ms !== undefined && action.cache_ttl_ms !== null ? action.cache_ttl_ms : '',
      area: action.area || defaultArea,
      prompt: action.prompt || ''
    });
    setQuestion(action.prompt || '');
    setSelectedEndpoints(action.selected_endpoints || action.endpoints || []);
    toast.success(`Cargada QA "${action.title}" en el probador. Modifica los datos y haz click en GUARDAR CAMBIOS.`);
  };

  const handleEditQuickAction = (action) => {
    setEditingActionId(action.id);
    setQaForm({
      title: action.title || '',
      description: action.description || '',
      icon: action.icon || 'BarChart3',
      color: action.color || 'blue',
      cache_ttl_ms: action.cache_ttl_ms !== undefined && action.cache_ttl_ms !== null ? action.cache_ttl_ms : '',
      area: action.area || defaultArea,
      prompt: action.prompt || ''
    });
    setQuestion(action.prompt || '');
    setSelectedEndpoints(action.selected_endpoints || action.endpoints || []);
    setQuickActionModalOpen(true);
  };

  const handleDeleteQuickAction = async (actionId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta Quick Action? Esta acción no se puede deshacer.')) return;
    try {
      const resp = await fetch(`${API_URL}/api/ai-config/quick-actions/${actionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('Error al eliminar la Quick Action');
      toast.success('Quick Action eliminada correctamente');
      fetchQuickActions();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Inputs
  const [question, setQuestion] = useState(`1. Radiografía general del inventario - Tiempo de Actualización: Diario
Analiza el inventario actual de CDSUR considerando:

- stock disponible
- ventas promedio mensual
- días de cobertura estimados
- quiebres históricos
- lead time de reposición

Necesito que me muestres:

1) Top 20 productos con mayor riesgo de quiebre
2) Top 20 productos con exceso de stock
3) Distribución de inventario por familia (papel, químicos, etc.)
4) Capital inmovilizado estimado en productos de baja rotación
5) Dónde están los mayores desbalances entre demanda y stock

Presentá el resultado con diagnóstico claro y explicación de las causas.
Objetivo
Entender si el problema es quiebres, exceso o mala distribución.`);
  const [tableContext, setTableContext] = useState('');
  const [prompt1, setPrompt1] = useState('Cargando prompt de generación...');
  const [prompt2, setPrompt2] = useState('Cargando prompt de análisis...');
  const [loadingPrompts, setLoadingPrompts] = useState(true);
  const [saving, setSaving] = useState(null); // 'prompt_mongo' | 'prompt_analysis' | null

  // Results
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState('analysis');
  const [error, setError] = useState(null);
  const [executionSteps, setExecutionSteps] = useState([]);
  const [resultsOpen, setResultsOpen] = useState(false);



  const handleSavePrompt = async (id, content) => {
    setSaving(id);
    try {
      const resp = await fetch(`${API_URL}/api/admin/mongo-prompts`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id, content })
      });
      if (!resp.ok) throw new Error('Error al guardar prompt');
      toast.success(`Prompt "${id}" actualizado como maestro`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(null);
    }
  };

  const handleResetPrompts = async () => {
    if (!window.confirm('¿Estás seguro de restablecer AMBOS prompts a los valores de fábrica? Se perderán tus cambios personalizados en la base de datos.')) return;
    setLoadingPrompts(true);
    try {
      const resp = await fetch(`${API_URL}/api/admin/mongo-prompts/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('Error al resetear prompts');
      toast.success('Prompts restablecidos a los valores por defecto del sistema');
      await fetchPrompts();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadingPrompts(false);
    }
  };

  const handleSaveQuickAction = async () => {
    if (!qaForm.title || !qaForm.description) {
      toast.warning('Título y descripción son obligatorios');
      return;
    }
    if (selectedEndpoints.length === 0) {
      toast.warning('Debe haber al menos un endpoint seleccionado');
      return;
    }
    
    const isEdit = editingActionId !== null;
    const url = isEdit 
      ? `${API_URL}/api/ai-config/quick-actions/${editingActionId}`
      : `${API_URL}/api/ai-config/quick-actions`;
    const method = isEdit ? 'PUT' : 'POST';
    const idValue = isEdit ? editingActionId : `qa_${Date.now()}`;

    try {
      const resp = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: idValue,
          title: qaForm.title,
          description: qaForm.description,
          icon: qaForm.icon,
          color: qaForm.color,
          prompt: qaForm.prompt,
          endpoints: selectedEndpoints,
          selected_endpoints: selectedEndpoints, // send both just to be safe
          prompt_mongo: prompt1,
          prompt_analysis: prompt2,
          cache_ttl_ms: qaForm.cache_ttl_ms ? parseInt(qaForm.cache_ttl_ms, 10) : null,
          area: qaForm.area,
          is_active: true,
          order: 99
        })
      });
      if (!resp.ok) throw new Error(`Error al ${isEdit ? 'actualizar' : 'guardar'} Quick Action`);
      toast.success(`Quick Action ${isEdit ? 'actualizada' : 'guardada'} exitosamente`);
      setQuickActionModalOpen(false);
      fetchQuickActions();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const fetchPrompts = useCallback(async () => {
    setLoadingPrompts(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/mongo-prompts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar prompts del servidor');
      const data = await res.json();
      setPrompt1(data.prompt_mongo || '');
      setPrompt2(data.prompt_analysis || '');
    } catch (err) {
      console.error('Error fetching prompts:', err);
      toast.error('No se pudieron cargar los prompts maestros de la base de datos');
    } finally {
      setLoadingPrompts(false);
    }
  }, [token]);

  const fetchEndpoints = useCallback(async () => {
    setEndpointsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/yiqi-import/endpoints`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar endpoints');
      const data = await res.json();
      setEndpoints(data);
      // Select all by default
      if (data && data.length > 0) {
        setSelectedEndpoints(data.map(ep => ep.id));
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setEndpointsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchEndpoints();
    fetchPrompts();
  }, [fetchEndpoints, fetchPrompts]);

  // Auto-close timer for execution modal
  useEffect(() => {
    let timer;
    if (!loading && isExecutionModalOpen && result && !error) {
      if (countdown === null) {
        setCountdown(5);
      } else if (countdown > 0) {
        timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
      } else {
        setIsExecutionModalOpen(false);
        setCountdown(null);
      }
    }

    if (!isExecutionModalOpen || loading || error) {
      if (countdown !== null) setCountdown(null);
    }

    return () => clearTimeout(timer);
  }, [loading, isExecutionModalOpen, result, error, countdown]);

  // Update table context when selections change to put the AI in context
  useEffect(() => {
    const RECS = {
      "reporte_de_pedidos_quiebres_actuales": "Quiebres y Demanda Insatisfecha",
      "stock_negativo": "Errores Operativos e Inconsistencias",
      "stock_auditoria": "Bloqueos Operativos",
      "material_venta_mensual_promedio": "Demanda y Rotación",
      "material_lead_time_sku": "Planificación y Reposición"
    };

    const selected = endpoints.filter(ep => selectedEndpoints.includes(ep.id));
    let text = "";
    selected.forEach(ep => {
      const ename = (ep.smartie_name || ep.entity_name || ep.id).replace(/ /g, '_').toUpperCase();
      let description = ep.description || 'Sin descripción';

      if (RECS[ep.id] && !description.includes('[RECOMENDACIÓN')) {
        description += ` [RECOMENDACIÓN: Punto de entrada para ${RECS[ep.id]}]`;
      }

      text += `## Collection: ${ename}\n`;
      text += `${description}\n`;
      text += `Columnas: ${ep.available_fields?.join(', ') || '(No descubiertas)'}\n\n`;
    });
    setTableContext(text.trim());
  }, [selectedEndpoints, endpoints]);

  const toggleEndpoint = (id) => {
    setSelectedEndpoints(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedEndpoints.length === endpoints.length) {
      setSelectedEndpoints([]);
    } else {
      setSelectedEndpoints(endpoints.map(ep => ep.id));
    }
  };

  const handleExecute = async () => {
    if (selectedEndpoints.length === 0) {
      toast.warning('Selecciona al menos un endpoint');
      return;
    }
    if (!question.trim()) {
      toast.warning('Ingresa una pregunta');
      return;
    }

    const startTime = Date.now();
    setLoading(true);
    setIsExecutionModalOpen(true);
    setError(null);
    setResult(null);
    setDuration(null);
    setResultsOpen(false);
    setExecutionSteps(['Iniciando proceso...', 'Preparando petición...']);

    try {
      const url = `${API_URL}/api/admin/mongo-query-test`;
      setExecutionSteps(prev => [...prev, `→ POST ${url}`]);

      let response;
      try {
        response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            user_question: question,
            selected_endpoints: selectedEndpoints,
            prompt_1_override: prompt1,
            prompt_2_override: prompt2
          })
        });
      } catch (networkErr) {
        // Network error: backend unreachable, CORS, or connection refused
        const detail = `Error de red al conectar con ${url}: ${networkErr.message}`;
        setExecutionSteps(prev => [...prev, `❌ ${detail}`]);
        throw new Error(detail);
      }

      setExecutionSteps(prev => [...prev, `← HTTP ${response.status} ${response.statusText}`]);

      let data;
      try {
        data = await response.json();
      } catch (parseErr) {
        throw new Error(`HTTP ${response.status} — respuesta no válida del servidor`);
      }

      if (!response.ok) {
        throw new Error(data.detail || data.error || `HTTP ${response.status}`);
      }

      setResult(data);
      if (data.execution_steps) {
        setExecutionSteps(data.execution_steps);
      }

      if (data.error) {
        setError(data.error);
        toast.error('La ejecución falló: ' + data.error);
        setActiveTab('query');
      } else {
        toast.success('Análisis completado');
        setActiveTab('analysis');
      }
    } catch (err) {
      setError(err.message);
      setExecutionSteps(prev => [...prev, `❌ Error final: ${err.message}`]);
      toast.error(err.message);
    } finally {
      const endTime = Date.now();
      setDuration((endTime - startTime) / 1000);
      setLoading(false);
    }

  };

  return (
    <div className="text-gray-200 min-h-screen font-sans selection:bg-orange-500/30">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 bg-[#1A1D27] p-6 rounded-xl border border-gray-800 shadow-xl mb-6">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent flex items-center gap-3">
              <MessageSquare className="w-7 h-7 text-[#00a1df]" />
              Analista MongoDB
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Testea el flujo de IA: Pregunta → Query Mongo → Ejecución → Análisis
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setDebugMode(!debugMode)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-xs font-black border transition-all ${debugMode
                  ? 'bg-orange-500/20 text-orange-400 border-orange-500'
                  : 'bg-gray-800 text-gray-500 border-gray-700 hover:bg-gray-700'
                }`}
            >
              <Bug className="w-4 h-4" />
              {debugMode ? 'OCULTAR DEBUG' : 'MODO DEBUG'}
            </button>
            <button
              onClick={() => {
                setQaForm(prev => ({
                  ...prev,
                  prompt: question
                }));
                setQuickActionModalOpen(true);
              }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-xs font-black text-white bg-blue-600 hover:bg-blue-500 transition-all border border-blue-700 shadow-lg cursor-pointer"
            >
              <Save className="w-4 h-4" />
              {editingActionId ? 'GUARDAR CAMBIOS (QA)' : 'GUARDAR COMO QA'}
            </button>
            <button
              onClick={handleExecute}
              disabled={loading || endpointsLoading}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-xs font-black text-white transition-all transform active:scale-95 shadow-lg ${loading ? 'bg-gray-700 cursor-not-allowed opacity-50' : 'bg-orange-600 hover:bg-orange-500 shadow-orange-900/20'
                }`}
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5 fill-current" />}
              {loading ? 'EJECUTANDO...' : 'EJECUTAR ANALISTA'}
            </button>
          </div>
        </div>

        {/* Quick Actions List (CRUD) */}
        <div className="bg-[#1A1D27] rounded-xl border border-gray-800 overflow-hidden shadow-xl">
          <button
            onClick={() => setQuickActionsOpen(!quickActionsOpen)}
            className="w-full px-6 py-4 border-b border-gray-800 bg-[#232733] hover:bg-gray-800/40 transition-colors flex justify-between items-center group cursor-pointer text-left focus:outline-none"
          >
            <span className="font-semibold text-sm uppercase tracking-wider text-gray-400 flex items-center gap-2">
              <ChevronDown className={`w-4 h-4 text-orange-500 transition-transform duration-200 ${quickActionsOpen ? 'rotate-180' : ''}`} />
              <Zap className="w-5 h-5 text-orange-500 shrink-0" />
              Acciones Rápida{quickActions.length !== 1 ? 's' : ''} (Quick Actions)
              <span className="ml-2 text-xs bg-orange-500/10 text-orange-400 px-2 py-0.5 rounded-full border border-orange-500/20 font-black">
                {quickActions.length}
              </span>
            </span>
            <span className="text-[10px] text-gray-500 font-mono tracking-widest font-bold group-hover:text-gray-300">
              {quickActionsOpen ? 'CONTRAER' : 'EXPANDIR'}
            </span>
          </button>
          
          {quickActionsOpen && (
            <div className="p-6 bg-[#0F1117]/40 border-t border-gray-800/50 animate-in slide-in-from-top-2 duration-200">
              {loadingActions ? (
                <div className="text-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-orange-500 opacity-50" />
                  <span className="text-xs text-gray-500 mt-2 block">Cargando Quick Actions...</span>
                </div>
              ) : quickActions.length === 0 ? (
                <div className="text-center py-8 text-gray-500 italic text-xs">
                  No hay Quick Actions creadas en el sistema.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {quickActions.map(action => {
                    const IconComponent = ICON_MAP[action.icon] || Package;
                    return (
                      <div 
                        key={action.id} 
                        className="border border-gray-800 bg-[#0F1117] rounded-xl p-4 flex items-start justify-between gap-4 group hover:border-gray-700 transition-all"
                      >
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <div className={`${COLOR_MAP[action.color] || 'bg-gray-500'} p-2 rounded-lg shrink-0`}>
                            <IconComponent className="h-5 w-5 text-white" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-white text-sm truncate">{action.title}</h4>
                              <span className="text-[9px] uppercase tracking-wider bg-gray-800 text-gray-400 px-2 py-0.5 rounded border border-gray-700 shrink-0">
                                {action.area}
                              </span>
                            </div>
                            <p className="text-xs text-gray-400 mt-1 line-clamp-2">{action.description}</p>
                            
                            {/* Endpoints indicators */}
                            {action.selected_endpoints && action.selected_endpoints.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2.5">
                                {action.selected_endpoints.slice(0, 3).map(ep => (
                                  <span key={ep} className="text-[8px] bg-[#1A1D27] text-gray-500 px-1.5 py-0.5 rounded border border-gray-800 truncate max-w-[120px]">
                                    {ep}
                                  </span>
                                ))}
                                {action.selected_endpoints.length > 3 && (
                                  <span className="text-[8px] bg-[#1A1D27] text-gray-500 px-1.5 py-0.5 rounded border border-gray-800">
                                    +{action.selected_endpoints.length - 3} más
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => handleLoadIntoTester(action)}
                            className="p-1.5 bg-gray-800/80 hover:bg-orange-600/20 text-gray-400 hover:text-orange-400 rounded-lg border border-gray-700/50 hover:border-orange-500/30 transition-colors cursor-pointer"
                            title="Cargar en el probador para testear y editar prompt/colecciones"
                          >
                            <Play className="w-3.5 h-3.5 fill-current" />
                          </button>
                          <button
                            onClick={() => handleEditQuickAction(action)}
                            className="p-1.5 bg-gray-800/80 hover:bg-blue-600/20 text-gray-400 hover:text-blue-400 rounded-lg border border-gray-700/50 hover:border-blue-500/30 transition-colors cursor-pointer"
                            title="Editar metadatos (Título, Color, Icono, Área...)"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeleteQuickAction(action.id)}
                            className="p-1.5 bg-gray-800/80 hover:bg-red-600/20 text-gray-400 hover:text-red-400 rounded-lg border border-gray-700/50 hover:border-red-500/30 transition-colors cursor-pointer"
                            title="Eliminar Quick Action"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Question Input */}
        <div className="bg-[#1A1D27] rounded-xl border border-gray-800 overflow-hidden shadow-xl">
          <div className="px-6 py-4 border-b border-gray-800 bg-[#232733] flex items-center gap-2">
            <Search className="w-5 h-5 text-orange-500" />
            <h3 className="font-semibold text-sm uppercase tracking-wider text-gray-400">Pregunta del Usuario</h3>
          </div>
          <div className="p-6">
            {editingActionId && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 flex items-center justify-between text-xs text-blue-400 mb-4 animate-in slide-in-from-top-2">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-blue-400 shrink-0" />
                  <span>
                    Modo de Edición Activo: Estás editando la Quick Action <strong>"{qaForm.title || editingActionId}"</strong>. Modifica la consulta o colecciones, pruébalas con el botón de ejecutar, y haz click en <strong>GUARDAR CAMBIOS</strong> para actualizarla.
                  </span>
                </div>
                <button
                  onClick={() => {
                    setEditingActionId(null);
                    setQaForm({ title: '', description: '', icon: 'BarChart3', color: 'blue', cache_ttl_ms: 3600000, area: defaultArea, prompt: '' });
                    toast.info('Modo de edición desactivado');
                  }}
                  className="text-blue-400 hover:text-white underline font-bold cursor-pointer font-sans"
                >
                  Cancelar Edición
                </button>
              </div>
            )}
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleExecute();
                }
              }}
              rows="20"
              placeholder="¿Cuál es el stock total de productos?"
              className="w-full bg-[#0F1117] border border-gray-700 rounded-lg p-5 text-white focus:outline-none focus:border-orange-500 transition-colors shadow-inner text-sm tracking-tight"
            />
          </div>
        </div>

        {/* Section: Config & Tables */}

        {/* Endpoints Selection */}
        {debugMode && (
          <div className="bg-[#1A1D27] rounded-xl border border-gray-800 overflow-hidden shadow-xl animate-in fade-in duration-300">
            <div className="px-6 py-4 border-b border-gray-800 bg-[#232733] flex justify-between items-center">
              <h3 className="font-semibold text-sm uppercase tracking-wider text-gray-400 flex items-center gap-2">
                <Table className="w-5 h-5 text-orange-500" /> Collections
              </h3>
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleSelectAll}
                  disabled={endpointsLoading || endpoints.length === 0}
                  className="text-[10px] px-3 py-1 rounded border transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-orange-500/10 text-orange-400 border-orange-500/30 hover:bg-orange-500/20 font-black"
                >
                  {selectedEndpoints.length === endpoints.length && endpoints.length > 0 ? 'DESELECCIONAR' : 'SELECCIONAR TODAS'}
                </button>
                <span className="text-[10px] bg-orange-500/10 text-orange-500 px-3 py-1 rounded-full border border-orange-500/20 font-black">
                  {selectedEndpoints.length} SELECCIONADAS
                </span>
              </div>
            </div>
            <div className="p-2 max-h-[300px] overflow-y-auto custom-scrollbar">
              {endpointsLoading ? (
                <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto opacity-50" /></div>
              ) : (
                endpoints.map(ep => (
                  <div
                    key={ep.id}
                    onClick={() => toggleEndpoint(ep.id)}
                    className={`group flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all mb-1 border ${selectedEndpoints.includes(ep.id)
                        ? 'bg-orange-500/10 border-orange-500/30'
                        : 'hover:bg-gray-800 border-transparent'
                      }`}
                  >
                    <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center transition-colors ${selectedEndpoints.includes(ep.id) ? 'bg-orange-500 border-orange-500' : 'border-gray-600'
                      }`}>
                      {selectedEndpoints.includes(ep.id) && <CheckCircle2 className="w-3 h-3 text-white" />}
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm font-medium leading-none ${selectedEndpoints.includes(ep.id) ? 'text-orange-400' : 'text-gray-300'}`}>
                        {ep.smartie_name || ep.entity_name}
                      </p>
                      <p className="text-[10px] text-gray-500 mt-1 truncate">{ep.description}</p>
                      {ep.available_fields && (
                        <div className="flex flex-wrap gap-1 mt-2 opacity-60 group-hover:opacity-100 transition-opacity">
                          {ep.available_fields.slice(0, 4).map(f => (
                            <span key={f} className="text-[8px] bg-gray-900 px-1 py-0.5 rounded text-gray-400">{f}</span>
                          ))}
                          {ep.available_fields.length > 4 && <span className="text-[8px] text-gray-500">+{ep.available_fields.length - 4}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Table Context Textarea */}
        {debugMode && (
          <div className="bg-[#1A1D27] rounded-xl border border-gray-800 overflow-hidden animate-in fade-in duration-300">
            <div className="px-4 py-3 border-b border-gray-800 bg-[#232733]">
              <h3 className="font-semibold text-sm uppercase tracking-wider text-gray-400">Contexto de Tablas</h3>
            </div>
            <textarea
              value={tableContext}
              readOnly
              placeholder="Selecciona endpoints para generar contexto..."
              className="w-full h-48 bg-transparent p-4 text-xs font-mono text-cyan-400 focus:outline-none resize-none opacity-80"
            />
          </div>
        )}

        {/* Prompt Maestro */}
        {debugMode && (
          <div className="space-y-6 animate-in fade-in duration-300">
            <div className="bg-[#1A1D27] rounded-xl border border-gray-800 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-800 bg-[#232733] flex items-center justify-between group">
                <h3 className="font-semibold text-sm uppercase tracking-wider text-gray-400 flex items-center gap-2">
                  <TextQuote className="w-5 h-5 text-orange-500" /> Prompt Maestro (Generación de Query)
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleResetPrompts}
                    className="text-[10px] bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white px-2 py-1 rounded border border-red-500/20 transition-all flex items-center gap-1 mr-2"
                    title="Restablecer ambos prompts a valores de fábrica"
                  >
                    <RefreshCw className={`w-3 h-3 ${loadingPrompts ? 'animate-spin' : ''}`} /> Restablecer Defaults
                  </button>
                  <button
                    onClick={fetchPrompts}
                    className="p-1 hover:bg-gray-800 rounded transition-colors text-gray-500 hover:text-orange-500"
                    title="Recargar de DB"
                  >
                    <Loader2 className={`w-3 h-3 ${loadingPrompts ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    onClick={() => handleSavePrompt('prompt_mongo', prompt1)}
                    disabled={saving === 'prompt_mongo' || loadingPrompts}
                    className="text-[10px] bg-gray-800 hover:bg-orange-600 hover:text-white px-3 py-1 rounded border border-gray-700 hover:border-orange-500 transition-all text-gray-500 font-bold tracking-tighter"
                  >
                    {saving === 'prompt_mongo' ? 'Guardando...' : 'Guardar como Maestro'}
                  </button>
                </div>
              </div>
              <div className="relative">
                {loadingPrompts && (
                  <div className="absolute inset-0 bg-[#0F1117]/50 flex items-center justify-center z-10 backdrop-blur-[1px]">
                    <Loader2 className="w-6 h-6 animate-spin text-orange-500 opacity-50" />
                  </div>
                )}
                <textarea
                  value={prompt1}
                  onChange={(e) => setPrompt1(e.target.value)}
                  disabled={loadingPrompts}
                  className="w-full h-48 bg-transparent p-4 text-sm font-sans text-gray-400 focus:outline-none focus:text-gray-200 transition-colors resize-y"
                />
              </div>
            </div>

            {/* Prompt Análisis */}
            <div className="bg-[#1A1D27] rounded-xl border border-gray-800 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-800 bg-[#232733] flex items-center justify-between group">
                <h3 className="font-semibold text-sm uppercase tracking-wider text-gray-400 flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-orange-500" /> Prompt Maestro (Análisis de Resultados)
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleResetPrompts}
                    className="text-[10px] bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white px-2 py-1 rounded border border-red-500/20 transition-all flex items-center gap-1 mr-2"
                    title="Restablecer ambos prompts a valores de fábrica"
                  >
                    <RefreshCw className={`w-3 h-3 ${loadingPrompts ? 'animate-spin' : ''}`} /> Restablecer Defaults
                  </button>
                  <button
                    onClick={fetchPrompts}
                    className="p-1 hover:bg-gray-800 rounded transition-colors text-gray-500 hover:text-orange-500"
                    title="Recargar de DB"
                  >
                    <Loader2 className={`w-3 h-3 ${loadingPrompts ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    onClick={() => handleSavePrompt('prompt_analysis', prompt2)}
                    disabled={saving === 'prompt_analysis' || loadingPrompts}
                    className="text-[10px] bg-gray-800 hover:bg-orange-600 hover:text-white px-3 py-1 rounded border border-gray-700 hover:border-orange-500 transition-all text-gray-500 font-bold tracking-tighter"
                  >
                    {saving === 'prompt_analysis' ? 'Guardando...' : 'Guardar como Maestro'}
                  </button>
                </div>
              </div>
              <div className="relative">
                {loadingPrompts && (
                  <div className="absolute inset-0 bg-[#0F1117]/50 flex items-center justify-center z-10 backdrop-blur-[1px]">
                    <Loader2 className="w-6 h-6 animate-spin text-orange-500 opacity-50" />
                  </div>
                )}
                <textarea
                  value={prompt2}
                  onChange={(e) => setPrompt2(e.target.value)}
                  disabled={loadingPrompts}
                  className="w-full h-48 bg-transparent p-4 text-sm font-sans text-gray-400 focus:outline-none focus:text-gray-200 transition-colors resize-y"
                />
              </div>
            </div>
          </div>
        )}

        {/* Result Area */}
        {(result || error) && (
          <div className="bg-[#1A1D27] rounded-xl border border-gray-800 shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex border-b border-gray-800 bg-[#232733] overflow-x-auto custom-scrollbar">
              {[
                { id: 'analysis', label: 'Análisis IA', icon: PieChart },
                { id: 'query', label: 'Query MongoDB', icon: Code },
                { id: 'data', label: 'Datos Crudos', icon: FileSearch },
                { id: 'steps', label: 'Trazabilidad', icon: Terminal }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-8 py-5 text-[10px] font-black uppercase tracking-[0.2em] transition-all relative shrink-0 ${activeTab === tab.id ? 'text-orange-500 bg-orange-500/5' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'
                    }`}
                >
                  <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? 'text-orange-500' : 'text-gray-600'}`} />
                  {tab.label}
                  {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-1 bg-orange-500 shadow-[0_-4px_10px_rgba(249,115,22,0.3)]" />}
                </button>
              ))}
            </div>

            <div className="p-6">
              {error && (
                <div className="mb-6 p-4 bg-red-900/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex gap-3">
                  <AlertCircle className="w-5 h-5 shrink-0" />
                  <div>
                    <p className="font-bold">Error en la ejecución:</p>
                    <p className="opacity-80">{error}</p>
                  </div>
                </div>
              )}

              {activeTab === 'analysis' && (
                <div className="max-w-none">
                  {duration && (
                    <div className="flex justify-end mb-4">
                      <span className="text-[10px] bg-sky-500/10 text-sky-400 px-3 py-1 rounded-full border border-sky-500/20 font-black flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5" /> TIEMPO DE RESPUESTA: {duration.toFixed(2)}s
                      </span>
                    </div>
                  )}
                  {result?.raw_results && result.raw_results.length > 0 && (
                    <div className="mb-8 bg-[#1A1D27]/50 rounded-xl border border-gray-800 overflow-hidden shadow-lg animate-in slide-in-from-top duration-300">
                      <button
                        onClick={() => setResultsOpen(!resultsOpen)}
                        className="w-full flex items-center justify-between p-4 bg-[#232733] hover:bg-gray-800/40 transition-colors group"
                      >
                        <span className="text-orange-400 font-bold text-sm uppercase flex items-center gap-3">
                          <ChevronDown className={`w-4 h-4 transition-transform ${resultsOpen ? 'rotate-180' : ''}`} />
                          Resultados ({result.raw_results.length})
                        </span>
                        <span className="text-[10px] text-gray-500 font-mono tracking-widest">{resultsOpen ? 'CONTRAER' : 'EXPANDIR'}</span>
                      </button>
                      {resultsOpen && (
                        <div className="p-6 border-t border-gray-800/40 bg-[#0F1117]/60">
                          <QueryResultTable data={result.raw_results} />
                        </div>
                      )}
                    </div>
                  )}

                  {result?.analysis ? (
                    <div className="text-gray-300 leading-relaxed text-sm pb-10 analysis-md">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {result.analysis}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <div className="py-12 text-center text-gray-600 italic">No hay análisis disponible.</div>
                  )}
                </div>
              )}

              {activeTab === 'query' && (
                <div className="relative group">
                  <pre className="bg-[#0F1117] p-4 rounded-lg border border-gray-800 text-cyan-400 font-mono text-xs overflow-x-auto shadow-inner">
                    {JSON.stringify(result?.generated_query, null, 2)}
                  </pre>
                  {result?.correction_log?.length > 0 && (
                    <div className="mt-4 p-3 bg-blue-900/10 border border-blue-500/20 rounded-lg">
                      <h4 className="text-[10px] font-bold text-blue-400 uppercase tracking-widest mb-2">Historial de correcciones</h4>
                      <ul className="space-y-1">
                        {result.correction_log.map((log, i) => (
                          <li key={i} className="text-[10px] text-blue-300/60 font-mono">{log}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'data' && (
                <div className="bg-[#0F1117] rounded-lg border border-gray-800 shadow-inner overflow-hidden">
                  <div className="max-h-[600px] overflow-auto custom-scrollbar">
                    {result?.raw_results && result.raw_results.length > 0 ? (
                      <table className="w-full text-xs text-left">
                        <thead className="bg-[#1A1D27] sticky top-0 uppercase tracking-tighter text-gray-500 font-bold border-b border-gray-800">
                          <tr>
                            {Object.keys(result.raw_results[0]).map(key => (
                              <th key={key} className="px-4 py-3">{key}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800">
                          {result.raw_results.map((row, i) => (
                            <tr key={i} className="hover:bg-gray-800/40 transition-colors">
                              {Object.keys(result.raw_results[0]).map(key => (
                                <td key={key} className="px-4 py-3 font-mono text-gray-400">
                                  {typeof row[key] === 'object' ? JSON.stringify(row[key]) : String(row[key] || '-')}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="py-12 text-center text-gray-600 italic">No hay datos retornados.</div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'steps' && (
                <div className="bg-[#0F1117] p-4 rounded-lg border border-gray-800 font-mono text-xs space-y-2 max-h-[500px] overflow-auto custom-scrollbar">
                  {executionSteps.length > 0 ? (
                    executionSteps.map((step, i) => (
                      <div key={i} className="flex gap-3 text-gray-400 border-l border-gray-800 pl-3">
                        <span className="text-orange-500/50 shrink-0">[{i + 1}]</span>
                        <span className="whitespace-pre-wrap">{step}</span>
                      </div>
                    ))
                  ) : (
                    <div className="py-12 text-center text-gray-600 italic">No hay logs de pasos disponibles.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Detailed Log Table (Persistent at bottom as requested) */}
        {debugMode && (
          <div className="bg-[#1A1D27] rounded-xl border border-gray-800 overflow-hidden shadow-xl animate-in fade-in duration-300">
            <div className="px-4 py-3 border-b border-gray-800 bg-[#232733] flex items-center justify-between">
              <h3 className="font-semibold text-sm uppercase tracking-wider text-gray-400 flex items-center gap-2">
                <Terminal className="w-4 h-4 text-orange-500" /> Trazabilidad de cada paso
              </h3>
              <span className="text-[10px] text-gray-500 font-mono">{executionSteps.length} Pasos</span>
            </div>
            <textarea
              readOnly
              value={executionSteps.length > 0 ? executionSteps.join('\n') : 'Esperando ejecución...'}
              placeholder="El log detallado aparecerá aquí después de ejecutar el flujo..."
              className="w-full h-48 bg-[#0F1117] p-4 text-xs font-mono text-green-500/80 focus:outline-none resize-none overflow-y-auto custom-scrollbar"
            />
          </div>
        )}

        {/* Legend / Info */}
        <div className="flex items-center gap-3 p-4 bg-orange-500/5 border border-orange-500/10 rounded-xl text-xs text-orange-400/80">
          <Info className="w-4 h-4 shrink-0" />
          <p>
            Este panel utiliza un bucle automático. Si la query generada por la IA falla, el sistema enviará el error de vuelta a la IA para una segunda oportunidad de corrección antes de mostrar el resultado final.
            La validación de seguridad es obligatoria y bloquea operadores riesgosos.
          </p>
        </div>

      </div>

      {/* Execution Traceability Modal */}
      {isExecutionModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-[#1A1D27] border border-gray-700 rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-gray-800 flex justify-between items-center bg-[#232733]">
              <h3 className="text-xl font-bold text-white flex items-center gap-3">
                <Terminal className="w-6 h-6 text-orange-500" />
                Ejecución del Analista
              </h3>
              {!loading && (
                <button
                  onClick={() => setIsExecutionModalOpen(false)}
                  className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-800 rounded-full"
                >
                  <X className="w-6 h-6" />
                </button>
              )}
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-3">
                  {loading ? (
                    <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  )}
                  <span className={loading ? "text-orange-400 animate-pulse font-medium" : "text-green-400 font-bold"}>
                    {loading ? 'Procesando consulta con IA...' : 'Proceso finalizado correctamente'}
                  </span>
                </div>
                {duration && (
                  <span className="text-gray-500 font-mono text-xs italic">
                    Tiempo total: {duration.toFixed(2)}s
                  </span>
                )}
              </div>

              <div className="bg-[#0F1117] rounded-xl border border-gray-800 overflow-hidden shadow-inner">
                <div className="px-4 py-2 border-b border-gray-800/50 bg-black/20 flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Consola de Trazabilidad</span>
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-red-500/50" />
                    <div className="w-2 h-2 rounded-full bg-amber-500/50" />
                    <div className="w-2 h-2 rounded-full bg-green-500/50" />
                  </div>
                </div>
                <textarea
                  readOnly
                  value={executionSteps.length > 0 ? executionSteps.join('\n') : 'Iniciando conexión...'}
                  className="w-full h-80 bg-transparent p-5 text-xs font-mono text-green-500/90 focus:outline-none resize-none overflow-y-auto custom-scrollbar leading-relaxed"
                />
              </div>
            </div>

            <div className="px-6 py-4 bg-[#232733]/50 flex justify-end gap-3 border-t border-gray-800">
              {!loading ? (
                <button
                  onClick={() => {
                    setIsExecutionModalOpen(false);
                    setCountdown(null);
                  }}
                  className="bg-orange-600 hover:bg-orange-500 text-white px-6 py-2.5 rounded-lg text-[10px] font-black transition-all shadow-lg shadow-orange-900/20 active:scale-95 flex items-center gap-2"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  CERRAR Y VER ANÁLISIS {countdown !== null && `(${countdown})`}
                </button>
              ) : (
                <div className="text-xs text-gray-500 italic flex items-center gap-2 px-4 py-2">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Por favor, esperá a que finalice el análisis...
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx="true">{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
          height: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #1A1D27;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #374151;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #4B5563;
        }
      `}</style>

      {/* Quick Action Save Modal */}
      {quickActionModalOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-[#1A1D27] border border-gray-700 rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-gray-800 flex justify-between items-center bg-[#232733]">
              <h3 className="text-lg font-bold text-white flex items-center gap-3">
                <Save className="w-5 h-5 text-blue-500" /> {editingActionId ? 'Editar Quick Action' : 'Guardar Quick Action'}
              </h3>
              <button onClick={() => setQuickActionModalOpen(false)} className="text-gray-400 hover:text-white transition-colors p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-6 text-sm text-gray-300">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Columna Izquierda: Formulario y Vista Previa */}
                <div className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold uppercase text-gray-500">Título</label>
                    <input
                      type="text"
                      value={qaForm.title}
                      onChange={e => setQaForm({ ...qaForm, title: e.target.value })}
                      className="w-full bg-[#0F1117] border border-gray-700 rounded-lg p-2 text-white text-xs focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <label className="text-xs font-bold uppercase text-gray-500">Descripción</label>
                    <textarea
                      rows="2"
                      value={qaForm.description}
                      onChange={e => setQaForm({ ...qaForm, description: e.target.value })}
                      className="w-full bg-[#0F1117] border border-gray-700 rounded-lg p-2 text-white text-xs resize-none focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <label className="text-xs font-bold uppercase text-gray-500">Prompt / Consulta IA</label>
                    <textarea
                      rows="4"
                      value={qaForm.prompt}
                      onChange={e => setQaForm({ ...qaForm, prompt: e.target.value })}
                      className="w-full bg-[#0F1117] border border-gray-700 rounded-lg p-2 text-white text-xs resize-y focus:outline-none focus:border-blue-500 font-mono scrollbar-thin"
                      placeholder="Escribe la consulta que procesará el analista..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-xs font-bold uppercase text-gray-500">Área</label>
                      <select
                        value={qaForm.area}
                        onChange={e => setQaForm({ ...qaForm, area: e.target.value })}
                        className="w-full bg-[#0F1117] border border-gray-700 rounded-lg p-2 text-white text-xs focus:outline-none focus:border-blue-500 animate-none capitalize"
                      >
                        {areasDisponibles.length === 0 && (
                          <option value="" disabled>Sin áreas disponibles</option>
                        )}
                        {areasDisponibles.map(area => (
                          <option key={area.id || area.name} value={area.name} className="capitalize">
                            {area.name}
                          </option>
                        ))}
                        {qaForm.area && !areasDisponibles.some(a => a.name === qaForm.area) && (
                          <option value={qaForm.area} className="capitalize">
                            {qaForm.area} (área eliminada)
                          </option>
                        )}
                      </select>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-bold uppercase text-gray-500">Cache TTL (ms)</label>
                      <input
                        type="number"
                        min="0"
                        value={qaForm.cache_ttl_ms}
                        onChange={e => setQaForm({ ...qaForm, cache_ttl_ms: e.target.value })}
                        className="w-full bg-[#0F1117] border border-gray-700 rounded-lg p-2 text-white text-xs focus:outline-none focus:border-blue-500"
                        placeholder="ej: 3600000"
                      />
                    </div>
                  </div>

                  {/* Vista Previa Card */}
                  <div className="space-y-2 pt-2">
                    <label className="text-xs font-bold uppercase text-gray-500">Vista Previa</label>
                    <div className="bg-[#0F1117] p-3 rounded-xl border border-gray-800">
                      <div className="border border-gray-800/80 bg-[#1A1D27] rounded-xl p-4 flex items-start gap-4 shadow-sm relative group max-w-full overflow-hidden">
                        <div className={`${COLOR_MAP[qaForm.color] || 'bg-gray-500'} p-2 rounded-lg shrink-0`}>
                          {(() => {
                            const PreviewIcon = ICON_MAP[qaForm.icon] || Package;
                            return <PreviewIcon className="h-5 w-5 text-white animate-none" />;
                          })()}
                        </div>
                        <div className="flex-1 min-w-0 animate-none">
                          <h3 className="font-semibold text-white text-sm mb-1 truncate">
                            {qaForm.title || 'Título de Quick Action'}
                          </h3>
                          <p className="text-xs text-gray-400 break-words line-clamp-2">
                            {qaForm.description || 'Descripción de la acción rápida'}
                          </p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-gray-500 mt-1 shrink-0 animate-none" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Columna Derecha: Selectores de Color e Icono */}
                <div className="space-y-4 flex flex-col h-full">
                  {/* Selector de Color */}
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase text-gray-500">Color</label>
                    <div className="flex flex-wrap gap-2">
                      {Object.keys(COLOR_MAP).map((colorKey) => {
                        const isSelected = qaForm.color === colorKey;
                        return (
                          <button
                            key={colorKey}
                            type="button"
                            onClick={() => setQaForm({ ...qaForm, color: colorKey })}
                            className={`w-7 h-7 rounded-full transition-all duration-200 hover:scale-110 active:scale-95 relative flex items-center justify-center cursor-pointer ${COLOR_MAP[colorKey]} ${
                              isSelected ? 'ring-2 ring-white ring-offset-2 ring-offset-[#1A1D27]' : ''
                            }`}
                            title={colorKey}
                          >
                            {isSelected && (
                              <span className="text-white text-xs font-black">✓</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Selector de Icono con Buscador */}
                  <div className="space-y-2 flex flex-col flex-1 min-h-[250px]">
                    <div className="flex justify-between items-center">
                      <label className="text-xs font-bold uppercase text-gray-500">Icono</label>
                      {iconSearch && (
                        <span className="text-[10px] bg-gray-800 text-gray-400 px-2 py-0.5 rounded-full font-mono">
                          {filteredIcons.length} encontrados
                        </span>
                      )}
                    </div>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                      <input
                        type="text"
                        placeholder="Buscar icono..."
                        value={iconSearch}
                        onChange={e => setIconSearch(e.target.value)}
                        className="w-full bg-[#0F1117] border border-gray-700 rounded-lg py-1.5 pl-8 pr-3 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                      />
                      {iconSearch && (
                        <button
                          onClick={() => setIconSearch('')}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-xs"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-5 gap-1.5 max-h-[200px] overflow-y-auto p-2 bg-[#0F1117] border border-gray-800 rounded-lg custom-scrollbar flex-1">
                      {filteredIcons.length === 0 ? (
                        <div className="col-span-5 text-center py-8 text-xs text-gray-500 italic">
                          No se encontraron iconos
                        </div>
                      ) : (
                        filteredIcons.map(([name, IconComponent]) => {
                          const isSelected = qaForm.icon === name;
                          return (
                            <button
                              key={name}
                              type="button"
                              onClick={() => setQaForm({ ...qaForm, icon: name })}
                              className={`p-2 rounded-lg flex flex-col items-center justify-center border transition-all duration-150 cursor-pointer ${
                                isSelected
                                  ? SELECTED_COLOR_CLASSES[qaForm.color] || 'bg-blue-500/20 border-blue-500 text-blue-400'
                                  : 'bg-transparent border-transparent hover:bg-gray-800/50 text-gray-400 hover:text-gray-200'
                              }`}
                              title={name}
                            >
                              <IconComponent className="w-5 h-5 shrink-0" />
                              <span className="text-[8px] mt-1 truncate max-w-full text-gray-500 group-hover:text-gray-300">
                                {name}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-800 bg-[#232733] flex justify-end gap-3">
              <button onClick={() => setQuickActionModalOpen(false)} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors">Cancelar</button>
              <button onClick={handleSaveQuickAction} className="px-6 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-500 rounded-lg shadow-lg shadow-blue-900/20 transition-all">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminChatTestPage;
